import type {
  CreativeBriefActiveRun,
  CreativeBriefCreated,
  CreativeBriefLatestResponse,
  CreativeBriefProgressEvent,
  CreativeBriefResult,
  CreativeBriefRunStatus,
  CreativeBriefSegmentRaw,
  CreativeBriefStatusResponse,
} from './brief.types'

const BRIEF_RUN_STATUSES = new Set<CreativeBriefRunStatus>([
  'queued',
  'running',
  'completed',
  'failed',
  'timeout',
])

function record(value: unknown, name: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`)
  }
  return value as Record<string, unknown>
}

function stringValue(value: unknown, name: string): string {
  if (typeof value !== 'string') throw new TypeError(`${name} must be a string`)
  return value
}

function nullableString(value: unknown, name: string): string | null {
  if (value === null || value === undefined) return null
  return stringValue(value, name)
}

function nullableNumber(value: unknown, name: string): number | null {
  if (value === null || value === undefined) return null
  if (typeof value !== 'number' || !Number.isFinite(value))
    throw new TypeError(`${name} must be a number`)
  return value
}

function stringArray(value: unknown, name: string): string[] {
  if (!Array.isArray(value)) throw new TypeError(`${name} must be an array`)
  return value.map((item, index) => stringValue(item, `${name}[${index}]`))
}

/** `segment_key`/deep segment fields stay loose (see `brief.types.ts`) — only the array shape matters. */
function segmentArray(value: unknown, name: string): CreativeBriefSegmentRaw[] {
  if (!Array.isArray(value)) throw new TypeError(`${name} must be an array`)
  return value.map(
    (item, index) => record(item, `${name}[${index}]`) as unknown as CreativeBriefSegmentRaw,
  )
}

export function decodeCreativeBriefResult(value: unknown): CreativeBriefResult {
  const row = record(value, 'brief result')
  return {
    execution_id: nullableString(row.execution_id, 'brief result.execution_id'),
    date_from: stringValue(row.date_from, 'brief result.date_from'),
    date_to: stringValue(row.date_to, 'brief result.date_to'),
    language: stringValue(row.language, 'brief result.language'),
    warnings: Array.isArray(row.warnings) ? stringArray(row.warnings, 'brief result.warnings') : [],
    segments: segmentArray(row.segments, 'brief result.segments'),
  }
}

function nullableResult(value: unknown, name: string): CreativeBriefResult | null {
  if (value === null || value === undefined) return null
  return decodeCreativeBriefResult(record(value, name))
}

function nullableRunStatus(value: unknown, name: string): CreativeBriefRunStatus | null {
  if (value === null || value === undefined) return null
  if (typeof value !== 'string' || !BRIEF_RUN_STATUSES.has(value as CreativeBriefRunStatus)) {
    throw new TypeError(`${name} has an unsupported value`)
  }
  return value as CreativeBriefRunStatus
}

function nullableActiveRun(value: unknown, name: string): CreativeBriefActiveRun | null {
  if (value === null || value === undefined) return null
  const row = record(value, name)
  return {
    execution_id: stringValue(row.execution_id, `${name}.execution_id`),
    segments_done: nullableNumber(row.segments_done, `${name}.segments_done`),
    segments_total: nullableNumber(row.segments_total, `${name}.segments_total`),
  }
}

export function decodeCreativeBriefLatestResponse(value: unknown): CreativeBriefLatestResponse {
  const row = record(value, 'brief latest response')
  return {
    active_run: nullableActiveRun(row.active_run, 'brief latest response.active_run'),
    status: nullableRunStatus(row.status, 'brief latest response.status'),
    execution_id: nullableString(row.execution_id, 'brief latest response.execution_id'),
    segments_done: nullableNumber(row.segments_done, 'brief latest response.segments_done'),
    segments_total: nullableNumber(row.segments_total, 'brief latest response.segments_total'),
    result: nullableResult(row.result, 'brief latest response.result'),
  }
}

export function decodeCreativeBriefCreated(value: unknown): CreativeBriefCreated {
  const row = record(value, 'brief created response')
  return { execution_id: stringValue(row.execution_id, 'brief created response.execution_id') }
}

export function decodeCreativeBriefStatusResponse(value: unknown): CreativeBriefStatusResponse {
  const row = record(value, 'brief status response')
  return { result: nullableResult(row.result, 'brief status response.result') }
}

export function decodeCreativeBriefProgressEvent(value: unknown): CreativeBriefProgressEvent {
  const row = record(value, 'brief progress event')
  const status = row.status
  if (typeof status !== 'string' || !BRIEF_RUN_STATUSES.has(status as CreativeBriefRunStatus)) {
    throw new TypeError('brief progress event.status has an unsupported value')
  }
  return {
    execution_id: stringValue(row.execution_id, 'brief progress event.execution_id'),
    status: status as CreativeBriefRunStatus,
    segments_done: nullableNumber(row.segments_done, 'brief progress event.segments_done'),
    segments_total: nullableNumber(row.segments_total, 'brief progress event.segments_total'),
    error: nullableString(row.error, 'brief progress event.error'),
  }
}
